import React, { Suspense } from 'react';
import { lazyWithRetry as lazy } from '../../utils/ui/lazyWithRetry';
import { useAuth } from '../../AuthContext';
import { BulkRunProvider } from '../bulkRun/BulkRunContext';
import { PullRefreshProvider } from '../pullRefresh/PullRefreshContext';
import { LifecycleActivationProvider } from '../lifecycleActivation/LifecycleActivationContext';
import useSessionToastReset from '../../components/common/toast/useSessionToastReset';
import { useSplash } from '../splash/SplashContext';
import { useSignOutSplashActive } from '../splash/signOutSplash';
import { useSignInCoverActive } from '../splash/signInCover';
import SessionTimeoutWarningHost from '../../components/auth/SessionTimeoutWarningHost';
import NoticeReceiptsSync from '../../components/session/NoticeReceiptsSync';
import ThemePreferenceSync from '../../components/session/ThemePreferenceSync';
import { useAuthHandoffBusy } from '../splash/authHandoffHold';

// Lazy: the Dock's bulk-progress modals pull in framer-motion (~545KB). Keeping it
// off the cold-start bundle. BulkRunProvider (run state) stays eager (above), so a
// backgrounded bulk send/download still survives in-app navigation; only the Dock's
// progress UI loads on demand (it renders nothing while idle anyway).
const BulkRunDock = lazy(() => import('../bulkRun/BulkRunDock'));
// Lazy: the lifecycle-activation Dock pulls in PeriodSummaryModal + JobDetail.
// The eager provider (LifecycleActivationProvider) holds only plain React state.
const LifecycleActivationDock = lazy(() => import('../lifecycleActivation/LifecycleActivationDock'));

/** Renders nothing while a sign-in handoff is in progress (authHandoffHold.js):
 *  the session-scoped receipts sync + dock chunks start after the next screen
 *  has painted, not inside the SIGNED_IN burst. Subscribes on its own so the
 *  shell above never re-renders for it. */
function DeferredWhileHandoff({ children }) {
  return useAuthHandoffBusy() ? null : children;
}

// Session-scoped provider stack + root-mounted docks. (Extracted verbatim from
// App.js 2026-08-15 — the file was over the 500-line cap; zero behavior change
// beyond adding useSessionToastReset.) Renders inside AuthProvider, so it
// reads the live session and:
//   • hands each dock provider a `signedIn` signal, so a backgrounded bulk
//     run / lifecycle-activation flow is CLEARED the moment the session ends
//     (logout, idle timeout, token expiry, cross-tab sign-out) — its in-memory
//     state (customer names, invoice numbers, amounts) can't outlive auth, and
//     can't resurrect if the user signs back in on the same tab (no reload).
//   • only MOUNTS the docks while signed in, so nothing holding tenant data can
//     paint over the login screen. Defense-in-depth beside the provider clear:
//     the clear stops same-tab resurrection; the mount gate protects any future
//     root dock that forgets to wire the clear.
//   • PullRefreshProvider also gets `signedIn` (defense-in-depth — no tenant
//     data today, but stays mounted through sign-out for AppShell's gesture
//     wiring) so it nulls both registration slots when signed out, refusing
//     a stale/future registrant's post-logout pull-to-refresh.
//   • clears the module-level toast store on sign-out (useSessionToastReset) —
//     error toasts never auto-dismiss and can carry tenant data, and the store
//     would otherwise survive into the NEXT sign-in on a shared device
//     (security review P1, 2026-08-15; same class as the 2026-07-01 incident).
export default function SessionScopedShell({ children }) {
  const { sessionUser, tenantId, isSuperAdmin } = useAuth();
  const signedIn = !!sessionUser;
  useSessionToastReset(signedIn);
  // Sign-out / sign-in handoffs live above the session-keyed tree, so the
  // splash stays up across the teardown or the landing until the other side
  // releases it (signOutSplash.js / signInCover.js).
  const signOutCover = useSignOutSplashActive();
  const signInCover = useSignInCoverActive();
  // The sign-out cover is a quiet veil (the sign-in ground, no wordmark / rail) so the
  // card comes back once, with no brand splash between (KAN-916); sign-in keeps the brand.
  useSplash(signOutCover || signInCover, { immediate: true, optional: true, veil: signOutCover && !signInCover });
  return (
    <LifecycleActivationProvider signedIn={signedIn}>
      <PullRefreshProvider signedIn={signedIn}>
        <BulkRunProvider signedIn={signedIn}>
          {children}
          {/* Idle-timeout warning: inside LocaleProvider (needs intl), every role. Mounted OUTSIDE the signedIn gate on purpose: at expiry its closing beat must outlive the session teardown. */}
          <SessionTimeoutWarningHost />
          <DeferredWhileHandoff>
          {signedIn && tenantId && (
            // RD-NOTICES-V2 — per-user, server-synced notice receipts (see
            // NoticeReceiptsSync's own header). Keyed so a tenant switch or a
            // sign-out/sign-in gets a fresh hydration rather than reusing the
            // previous session's in-flight state. Renders nothing.
            <NoticeReceiptsSync key={`${sessionUser.id}:${tenantId}`} userId={sessionUser.id} tenantId={tenantId} />
          )}
          </DeferredWhileHandoff>
          <DeferredWhileHandoff>
          {/* KAN-304: the signed-in user's saved theme (tenant users: profiles, platform: platform_users). One read per sign-in; renders nothing. */}
          {signedIn && (
            <ThemePreferenceSync key={`${sessionUser.id}:${isSuperAdmin === true}`} userId={sessionUser.id} isPlatformUser={isSuperAdmin === true} />
          )}
          </DeferredWhileHandoff>
          <DeferredWhileHandoff>
          {signedIn && (
            <>
              {/* Root-mounted bulk-run surface (progress modal / persistent
                  pill). Lives ABOVE the tab-keyed shell so a backgrounded
                  bulk send/download survives in-app navigation. Renders
                  nothing while idle. */}
              <Suspense fallback={null}>
                <BulkRunDock />
              </Suspense>
              {/* Root-mounted lifecycle-activation review flow (PeriodSummaryModal
                  + JobDetail). Renders nothing while idle. Lazy so the heavy UI
                  (PeriodSummaryModal, JobDetail) stays off the cold-start bundle;
                  the eager provider above keeps state alive across tab switches. */}
              <Suspense fallback={null}>
                <LifecycleActivationDock />
              </Suspense>
            </>
          )}
          </DeferredWhileHandoff>
        </BulkRunProvider>
      </PullRefreshProvider>
    </LifecycleActivationProvider>
  );
}
